/* eslint-disable react/no-unescaped-entities */
import Footer from "@buddieshr/components/footer";
import Header from "@buddieshr/components/header";
import MetaHeader from "@buddieshr/components/metaHeader";
import PageWrapper from "@buddieshr/components/templates/pageWrapper";
import { useRouter } from "next/router";
import React from "react";
import styles from "@buddieshr/styles/use-cases/index.module.scss";
import { Alert, Button, Tag } from "antd";
import TextBackground from "@buddieshr/components/text/textBackground";
import Image from "next/image";
import AddToSlackButton from "@buddieshr/components/addToSlackButton";
import { PRODUCTS } from "@buddieshr/utils/constants-products";
import PricingButtonHint from "@buddieshr/components/pricing/pricingbuttonHint";
import Link from "next/link";
import { CrownOutlined } from "@ant-design/icons";
import G2Logos from "@buddieshr/components/g2Logos";

const Feedback = () => {
  const router = useRouter();

  const app = PRODUCTS.find((p) => p.slug === "pulsy");

  return (
    <>
      <MetaHeader
        title={"Slack app for Gathering Employee Feedback | BuddiesHR"}
        description={
          "Gather employee feedback in Slack with Pulsy. Get anonymous feedback and run surveys and polls easily."
        }
        currentURL={`https://buddieshr.com/${
          router.locale === "en" ? "" : router.locale
        }/use-cases/feedback`}
        currentShare={`https://buddieshr.com/img/share_preview_1.png`}
        pageSlug={`use-cases/feedback`}
        locale={router.locale}
      />
      <Header />
      <div className={styles.container}>
        <PageWrapper width={800}>
          <div className={styles.titlePanel}>
            <div className={styles.tagWrapper}>
              <Tag
                size="large"
                color="blue"
                bordered={false}
                style={{
                  fontSize: 16,
                  padding: "4px 16px",
                  fontWeight: "bold",
                }}
              >
                USE CASE
              </Tag>
            </div>
            <h1 className={styles.title}>Gather employee feedback in Slack</h1>
            <p className={styles.subtitle}>
              BuddiesHR can be used to gather employee feedback directly in
              Slack thanks to its app called Pulsy. In this page you'll learn
              why it's important to gather employee feedback, why Slack is the
              perfect place for that and how to concretly gather employee
              feedback (anonymously or not).
            </p>
            <div className={styles.topCta}>
              <div className={styles.topCtaContainer}>
                <AddToSlackButton
                  app={app}
                  style={{ width: 240 }}
                  text={"Add to Slack"}
                />
                <PricingButtonHint />
              </div>
            </div>
          </div>

          <Image
            src="/img/animated-emojis/Left%20Speech%20Bubble.png"
            alt="Left Speech Bubble"
            width="125"
            height="125"
            className={styles.topIconLeft}
          />
          <Image
            src="/img/animated-emojis/Left%20Speech%20Bubble.png"
            alt="Speech Balloon"
            width="125"
            height="125"
            className={styles.topIconRight}
          />
        </PageWrapper>

        <div className={styles.section}>
          <PageWrapper width={800}>
            <Image
              src="/img/animated-emojis/Ear.png"
              alt="Ear"
              width="138"
              height="138"
              className={styles.ear}
            />
            <h2>Why is it important to gather employee feedback? </h2>
            <p>
              It is important to keep the door open for anonymous feedback as
              well as run feedback surveys regularly in order to{" "}
              <TextBackground>
                listen to what employees have to say.
              </TextBackground>
              <br />
              <br />
              Having an employee feedback culture has many benefits:
            </p>
            <ul className={styles.listBold}>
              <li>It builds Trust and Loyalty</li>
              <li>{"You're able to anticipate employee burnout"}</li>
              <li>It can prevent employee churn</li>
            </ul>
            <br />
            <br />
            <h2>
              Why slack is the right place for asking feedback to your
              employees?
            </h2>
            <div>
              <div className={styles.slackRightPlaceElement}>
                <div className={styles.iconLeft}>✅</div>
                <div className={styles.textRightPlaceElement}>
                  Asking for feedback is something that HRs have to do
                  regularly.
                  <b>
                    If employees have to make an extra effort to submit
                    anything, they will either postpone it or won't post
                    anything.
                  </b>
                </div>
              </div>

              <div className={styles.slackRightPlaceElement}>
                <div className={styles.iconLeft}>✅</div>
                <div className={styles.textRightPlaceElement}>
                  Asking the feedback directly in the context of Slack without
                  having to sign-in to any extra account or click on a link
                  makes sure that <b>the response rate is maximized.</b>
                  <Alert
                    style={{ marginTop: 10 }}
                    icon={<CrownOutlined />}
                    showIcon
                    type="success"
                    bordered={false}
                    message={
                      "Our customers regularly achieve 95%+ response rate using Pulsy"
                    }
                  />
                </div>
              </div>

              <div className={styles.slackRightPlaceElement}>
                <div className={styles.iconLeft}>✅</div>
                <div className={styles.textRightPlaceElement}>
                  It also makes sure to{" "}
                  <b>stay in the context of their workplace</b> and therefore
                  that answers are relevant.
                </div>
              </div>

              <div className={styles.slackRightPlaceElement}>
                <div className={styles.iconLeft}>👉</div>
                <div className={styles.textRightPlaceElement}>
                  Slack is the right place for collecting employee feedback
                  because it's{" "}
                  <b>
                    quick to submit and requires very low effort and no
                    de-focus.
                  </b>
                </div>
              </div>
            </div>
          </PageWrapper>
        </div>
        <div className={`${styles.section} ${styles.section2}`}>
          <PageWrapper width={800}>
            <Image
              src="/img/animated-emojis/Writing%20Hand.png"
              alt="Writing Hand"
              width="138"
              height="138"
              className={styles.writing}
            />
            <h2>How Buddieshr can be used to gather feedback?</h2>
            <p>
              By installing Pulsy Slack app from BuddiesHR, you'll be able to
              run recurring surveys asking for feedback.
              <br />
              <br />
              Here are the steps to start collecting anonymous feedback in
              Slack:
            </p>
            <ul className={styles.listBold}>
              <li>
                <TextBackground>Step 1</TextBackground>: Install{" "}
                <Link target="_blank" href={app.installSlackLink}>
                  Pulsy Survey for Slack
                </Link>
              </li>
              <li>
                <TextBackground>Step 2</TextBackground>: Create a recurring
                survey (eNPS weekly for instance)
              </li>
              <li>
                <TextBackground>Step 3</TextBackground>: Publish the survey and
                start collecting feedback.
              </li>
            </ul>
            <div className={styles.productPic}>
              <div className={styles.productPicInner}>
                <div className={styles.productPic1Wrapper}>
                  <Image
                    src="/img/apps/pulsy/product/screen_answer_1.png"
                    width={511}
                    height={560}
                    alt="pulse survey in slack"
                    className={styles.pic}
                  />
                </div>
                <div className={styles.productPic2Wrapper}>
                  <Image
                    src="/img/apps/pulsy/product/screen_answer_2.png"
                    width={514}
                    height={382}
                    alt="enps in slack"
                    className={styles.pic}
                  />
                </div>
              </div>
              <div className={styles.productPicInner}>
                <div className={styles.productPic3Wrapper}>
                  <Image
                    src="/img/apps/pulsy/product/dashboard.png"
                    width={563 * 0.9}
                    height={572 * 0.9}
                    alt="dashboard answers in slack"
                    className={styles.pic}
                  />
                </div>
              </div>
            </div>
            <br />
            <br />
            <h2>
              Why Pulsy is the best option for collecting employee feedback?
            </h2>
            <p>
              Pulsy has all the features to make employee feedback easy. It's
              fully integrated into Slack:
            </p>
            <ul className={styles.listBold}>
              <li>
                You can ask for clarifications while keeping the conversation
                anonymous
              </li>
              <li>Visualize the answers in a dashboard</li>
              <li>Be notified for new submissions</li>
              <li>Access a question bank with dozens of examples</li>
              <li>Track eNPS over time </li>
              <li>
                <div style={{ display: "flex", flexWrap: "wrap" }}>
                  Access incomplete responses{" "}
                  <Tag
                    color="volcano"
                    bordered={false}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginLeft: 16,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 18,
                        marginRight: 6,
                      }}
                    >
                      🌟
                    </div>
                    <div style={{ fontWeight: "bold" }}>
                      {`Pulsy's super power!`}
                    </div>
                  </Tag>
                </div>
              </li>
              <li>
                <div style={{ display: "flex", flexWrap: "wrap" }}>
                  Have an anonymous chat with responders{" "}
                  <Tag
                    color="volcano"
                    bordered={false}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginLeft: 16,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 18,
                        marginRight: 6,
                      }}
                    >
                      🌟
                    </div>
                    <div style={{ fontWeight: "bold" }}>
                      {`Pulsy's super power!`}
                    </div>
                  </Tag>
                </div>
              </li>
              <li>
                {
                  "Employees don't leave Slack to answer and don't have to create any other account."
                }
              </li>
            </ul>
            <div className={styles.cta}>
              <Link href="/pulsy" alt="gather employee feedback in slack">
                <Button type="primary" size="large" style={{ minWidth: 200 }}>
                  Learn more
                </Button>
              </Link>
            </div>
            <div style={{ marginTop: 44 }}>
              <h3 style={{ textAlign: "center", color: "#628fa2" }}>
                Pulsy is a top performing app on Slack
              </h3>
              <G2Logos
                withTitle={false}
                withBackground={false}
                style={{ marginBottom: 0 }}
                className={styles.blackone}
              />
            </div>
          </PageWrapper>
        </div>
        <PageWrapper width={900}>
          <div className={styles.installWrapper}>
            <div className={styles.installInner}>
              <div className={styles.installContentContainer}>
                <div>
                  <Image
                    src="/img/apps/pulsy/logo.png"
                    alt="Pulsy survey for employee feedback in slack"
                    width={120}
                    height={120}
                  />
                </div>
                <div className={styles.installContentRight}>
                  <h2>
                    Gather Employee Feedback directly from Slack with Pulsy
                  </h2>
                  <div>No external tool to sign-up to, very simple set up.</div>
                  <div style={{ marginTop: 18 }}>
                    <AddToSlackButton
                      block
                      app={PRODUCTS.find((p) => p.slug === "pulsy")}
                    />
                    <PricingButtonHint />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.faq}>
            <h2>FAQ</h2>
            <div className={styles.faqWrapper}>
              <div className={styles.faqElement}>
                <h3>
                  Collecting anonymous feedback versus non-anonymous feedback
                </h3>
                <p>
                  Running anonymous feedbacks is proven to give more{" "}
                  <TextBackground>contructive answers</TextBackground>.
                </p>
              </div>
              <div className={styles.faqElement}>
                <h3>
                  What's the good frequency for asking anonymous employee
                  feedback?
                </h3>
                <p>
                  <TextBackground>Every 2 weeks</TextBackground> or once a month
                  is a good frequency for asking feedback to the employees.
                </p>
              </div>
            </div>
          </div>
        </PageWrapper>
      </div>

      <Footer />
    </>
  );
};

export default Feedback;
